import type { DropResult } from "@hello-pangea/dnd";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Autocomplete,
	Box,
	Button,
	Chip,
	Collapse,
	FormControlLabel,
	IconButton,
	List,
	ListItem,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import type { MetadataApiProps } from "../../../../features/product/types/report-template";
import {
	addBlock,
	type BlockSummary,
	listSections,
	moveBlock,
	moveField,
	removeBlock,
	summarizeBlocks,
	summaryLabelsForBlock,
	type TemplateBlock,
	type TemplateConfig,
	updateBlock,
} from "../../../../features/product/utils/template-config";
import ModalDelete from "../../../../shared/components/modal-delete";
import AddBlockDialog from "./AddBlockDialog";
import TemplateBlockFields from "./TemplateBlockFields";

type TemplateVisualEditorProps = {
	config: TemplateConfig;
	metadata: MetadataApiProps[];
	onPreview: () => void;
	onChange: (config: TemplateConfig) => void;
};

function removeMessage(config: TemplateConfig, block: BlockSummary): string {
	const question = `Remover o bloco "${block.title}" do template?`;
	const labels = summaryLabelsForBlock(config, block.id);
	if (labels.length === 0) return question;
	return `${question} Ele é usado no resumo do extrato (${labels.join(", ")}), que vai ficar sem esses dados.`;
}

export default function TemplateVisualEditor({
	config,
	metadata,
	onPreview,
	onChange,
}: TemplateVisualEditorProps) {
	const [removingIndex, setRemovingIndex] = useState<number | null>(null);
	const [expandedKey, setExpandedKey] = useState<string | null>(null);
	const [adding, setAdding] = useState(false);

	const blocks = useMemo(
		() => summarizeBlocks(config, metadata),
		[config, metadata],
	);
	const removingBlock = removingIndex !== null ? blocks[removingIndex] : null;

	function changeBlock(index: number, changes: Partial<TemplateBlock>) {
		onChange(updateBlock(config, index, changes));
	}

	function handleDragEnd(result: DropResult) {
		if (!result.destination) return;
		if (result.destination.index === result.source.index) return;
		if (result.type === "BLOCK") {
			onChange(
				moveBlock(config, result.source.index, result.destination.index),
			);
			return;
		}
		const blockIndex = Number(result.type.replace("FIELD-", ""));
		const block = config.blocks?.[blockIndex];
		if (!block) return;
		changeBlock(blockIndex, {
			fields: moveField(block, result.source.index, result.destination.index),
		});
	}

	function handleAdd(apiId: string, responseBlock: string) {
		const next = addBlock(config, apiId, responseBlock);
		onChange(next);
		setExpandedKey(summarizeBlocks(next, metadata).at(-1)?.key ?? null);
	}

	function confirmRemove() {
		if (removingIndex === null) return;
		onChange(removeBlock(config, removingIndex));
		setRemovingIndex(null);
	}

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					alignItems: { xs: "stretch", sm: "center" },
					justifyContent: "space-between",
					gap: 1,
					mb: 1,
				}}
			>
				<Typography color="text.secondary">
					{blocks.length === 1
						? "1 bloco"
						: `${blocks.length} blocos · arraste pela alça para mudar a ordem`}
				</Typography>
				<Box
					sx={{
						display: "flex",
						gap: 1,
						"& > *": { flex: { xs: 1, sm: "none" } },
					}}
				>
					<Button variant="outlined" type="button" onClick={onPreview}>
						Preview
					</Button>
					<Button
						variant="outlined"
						type="button"
						onClick={() => setAdding(true)}
					>
						Adicionar bloco
					</Button>
				</Box>
			</Box>

			{blocks.length === 0 ? (
				<Typography color="text.secondary" sx={{ py: 2 }}>
					Nenhum bloco no template.
				</Typography>
			) : (
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="template-blocks" type="BLOCK">
						{(droppable) => (
							<List
								disablePadding
								ref={droppable.innerRef}
								{...droppable.droppableProps}
							>
								{blocks.map((block, index) => (
									<Draggable
										key={block.key}
										draggableId={block.key}
										index={index}
									>
										{(draggable, snapshot) => (
											<ListItem
												ref={draggable.innerRef}
												{...draggable.draggableProps}
												sx={{
													mb: 1,
													border: "1px solid",
													borderColor: snapshot.isDragging
														? "primary.main"
														: "divider",
													borderRadius: 1,
													bgcolor: "background.paper",
													boxShadow: snapshot.isDragging ? 4 : 0,
													display: "block",
													px: { xs: 1, sm: 2 },
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														flexWrap: "wrap",
														columnGap: 2,
														rowGap: 1,
													}}
												>
													<Box
														{...draggable.dragHandleProps}
														role="button"
														aria-label={`Arrastar ${block.title}`}
														sx={{
															display: "flex",
															color: "text.secondary",
															cursor: "grab",
														}}
													>
														<DragIndicatorIcon />
													</Box>
													<Box
														sx={{
															flex: "1 1 0",
															minWidth: 0,
															order: 1,
															opacity: block.active ? 1 : 0.5,
														}}
													>
														<Typography fontWeight={600}>
															{block.title}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
														>
															{block.section}
														</Typography>
													</Box>
													<Box
														sx={{
															display: { xs: "block", md: "none" },
															flexBasis: "100%",
															height: 0,
															order: 3,
														}}
													/>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: { md: "flex-end" },
															flexWrap: "wrap",
															gap: 1,
															flex: { xs: "1 1 100%", md: "0 1 auto" },
															minWidth: 0,
															order: { xs: 4, md: 2 },
															pl: { xs: 5, md: 0 },
															textAlign: { md: "right" },
														}}
													>
														{block.simple ? (
															<Typography
																variant="body2"
																color="text.secondary"
															>
																{`${block.fieldCount} ${block.fieldCount === 1 ? "campo" : "campos"} · API ${block.apiName}`}
															</Typography>
														) : (
															<>
																<Typography
																	variant="body2"
																	color="text.secondary"
																>
																	{block.apiName}
																</Typography>
																<Chip
																	size="small"
																	variant="outlined"
																	label="Avançado · editar no JSON"
																/>
															</>
														)}
													</Box>
													<FormControlLabel
														label={block.active ? "Ativo" : "Inativo"}
														sx={{
															mr: 0,
															minWidth: 96,
															pl: { xs: 5, md: 0 },
															order: { xs: 5, md: 3 },
														}}
														control={
															<Switch
																size="small"
																checked={block.active}
																onChange={(event) =>
																	changeBlock(index, {
																		status: event.target.checked
																			? "active"
																			: "inactive",
																	})
																}
																inputProps={{
																	"aria-label": `Bloco ${block.title} ativo`,
																}}
															/>
														}
													/>
													<Box
														sx={{
															display: "flex",
															gap: 2,
															width: 76,
															flexShrink: 0,
															order: { xs: 2, md: 4 },
														}}
													>
														<Tooltip title="Remover bloco">
															<IconButton
																size="small"
																aria-label={`Remover ${block.title}`}
																onClick={() => setRemovingIndex(index)}
															>
																<DeleteOutlineIcon fontSize="small" />
															</IconButton>
														</Tooltip>
														{block.simple && (
															<IconButton
																size="small"
																aria-label={`Editar ${block.title}`}
																aria-expanded={expandedKey === block.key}
																onClick={() =>
																	setExpandedKey(
																		expandedKey === block.key
																			? null
																			: block.key,
																	)
																}
															>
																{expandedKey === block.key ? (
																	<ExpandLessIcon fontSize="small" />
																) : (
																	<ExpandMoreIcon fontSize="small" />
																)}
															</IconButton>
														)}
													</Box>
												</Box>
												{block.simple && (
													<Collapse
														in={expandedKey === block.key}
														timeout="auto"
														unmountOnExit
													>
														<Box
															sx={{
																display: "flex",
																flexWrap: "wrap",
																gap: 2,
																pt: 2,
																pl: { xs: 0, sm: 5 },
															}}
														>
															<TextField
																label="Título"
																size="small"
																value={block.title}
																onChange={(event) =>
																	changeBlock(index, {
																		title: event.target.value,
																	})
																}
																sx={{ flex: "1 1 240px" }}
															/>
															<Autocomplete
																freeSolo
																options={listSections(config, index)}
																inputValue={block.section}
																onInputChange={(_, value) =>
																	changeBlock(index, { section: value })
																}
																sx={{ flex: "1 1 240px" }}
																renderInput={(params) => (
																	<TextField
																		{...params}
																		label="Seção"
																		size="small"
																	/>
																)}
															/>
														</Box>
														{config.blocks?.[index] && (
															<TemplateBlockFields
																block={config.blocks[index]}
																blockIndex={index}
																apiName={block.apiName}
																metadata={metadata}
																onChange={(fields) =>
																	changeBlock(index, { fields })
																}
															/>
														)}
													</Collapse>
												)}
											</ListItem>
										)}
									</Draggable>
								))}
								{droppable.placeholder}
							</List>
						)}
					</Droppable>
				</DragDropContext>
			)}

			<AddBlockDialog
				open={adding}
				metadata={metadata}
				onClose={() => setAdding(false)}
				onAdd={handleAdd}
			/>

			<ModalDelete
				open={removingBlock !== null}
				textBody={removingBlock ? removeMessage(config, removingBlock) : ""}
				handleModalClose={() => setRemovingIndex(null)}
				handleDelete={confirmRemove}
				loading={false}
			/>
		</Box>
	);
}
